import { LoadableComponent } from '@loadable/component';
import React, { CSSProperties } from 'react';

import { Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  Button, Stack, HeaderText, BodyText, Modal, Icon,
} from '../../ui';
import SVGatorAnimation from '../../ui/SVGatorAnimation';
import Image from '../../shared/Image';
import CloseIcon from '../../../../images/icons/icon-close.svg';

import type {
  IBodyTextProps,
  IHeaderTextProps,
  StackProps,
  IButtonProps,
} from '../../ui';

type BackgroundComponentProps = {
  style: CSSProperties,
};

type CelebrationModalHeadlineProps = {
  text: string;
  highlightedText?: string;
  highlightColor?: string;
  style?: CSSProperties;
};

export type CelebrationModalProps = {
  image?: {
    animated?: boolean;
    source: string;
    width: number;
    height: number;
    frameSize: number;
    marginBottom?: number;
  };
  background?: {
    component: React.FC<BackgroundComponentProps> | LoadableComponent<any>,
    style: CSSProperties,
  }
  headline: IHeaderTextProps & CelebrationModalHeadlineProps;
  body?: IBodyTextProps & { text: string };
  button: IButtonProps & { text: string };
  stack?: StackProps;
  close?: () => void;
  testIdPrefix?: string;
  Child?: any;
  childProps?: any;
  closeButtonColor?: string;
};

const CelebrationModal: React.FC<CelebrationModalProps> = ({
  image,
  headline,
  body,
  button,
  close,
  testIdPrefix,
  stack = {},
  Child,
  childProps = {},
  closeButtonColor,
  background,
}) => {
  const ImageComponent = image && image.animated ? SVGatorAnimation : Image;

  const buttonStyle = stack.color ? {
    zIndex: 1,
  } : {
    width: '100%',
    border: `2px solid ${colors.whiteWash}`,
    boxSizing: 'border-box',
    zIndex: 1,
  };

  return (
    <Modal
      appearance="fullScreen"
    >
      <Box
        color={stack.color || colors.blackInBlack}
        style={{ height: '100%' }}
        padding={24}
        flex={1}
      >
        {background && <background.component style={background.style} />}
        <Stack
          space={stack.space || 24}
          justifyContent={stack.justifyContent || 'flex-end'}
          alignItems={stack.alignItems || 'stretch'}
          paddingX={stack.paddingX || 0}
          paddingY={stack.paddingY || 64}
          paddingBottom={stack.paddingBottom}
          flex={1}
          flexBasis="auto"
        >
          {image && (
            <Box
              justifyContent="center"
              alignItems="center"
              color={colors.whiteWash}
              style={{
                margin: 'auto',
                borderRadius: image.frameSize,
                width: image.frameSize,
                height: image.frameSize,
                marginBottom: image.marginBottom,
              }}
            >
              <ImageComponent
                source={{
                  uri: image.source,
                  width: image.width,
                  height: image.height,
                }}
                testID={`${testIdPrefix}_image`}
              />
            </Box>
          )}
          {headline && (
            <HeaderText
              color={headline.color || colors.whiteWash}
              textAlign={headline.textAlign || 'center'}
              testID={`${testIdPrefix}_header`}
              level={headline.level || 1}
              style={headline.style}
            >
              {headline.text}
              {headline.highlightedText && (
                <HeaderText
                  color={headline.highlightColor}
                >
                  {headline.highlightedText}
                </HeaderText>
              )}
            </HeaderText>
          )}
          {body && (
            <BodyText
              color={body.color || colors.whiteWash}
              textAlign={body.textAlign || 'center'}
              testID={`${testIdPrefix}_body`}
              style={body.style}
            >
              {body.text}
            </BodyText>
          )}
          {Child && (<Child {...childProps} />)}
        </Stack>
        <Button
          style={buttonStyle}
          appearance={button.appearance || 'primary'}
          onClick={() => button.onClick?.()}
          testID={`${testIdPrefix}_cta`}
        >
          {button.text}
        </Button>
        <Button
          appearance="none"
          onClick={() => close?.()}
          style={{ position: 'absolute', top: 20, right: 20 }}
          testID={`${testIdPrefix}_close`}
        >
          <Icon
            uri={CloseIcon}
            size={20}
            style={{ tintColor: closeButtonColor || colors.whiteWash }}
          />
        </Button>
      </Box>
    </Modal>
  );
};

export default CelebrationModal;
